import { cloneDeep } from "lodash-es";
import * as React from "react";
import { Button, Input, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import {
    GetLicenciasSinCredencialesResponse, GuardarCredencialLicenciaResponse, LicenciaSinCredencialItem
} from "../models/dataModels";
import { AlertsService } from "../utils/AlertsService";
import { ApiFetcher } from "../utils/ApiFetcher";
import { ClientContext } from "../utils/ClientContext";
import { I18n } from "../utils/I18n";
import { CancelIcon, CheckCircleIcon, CheckIcon, SpinnerIcon } from "../utils/Icons";
import { logAndExtractInfoFromException } from "../utils/ValuesFormatter";

/**
 * Situación de una fila del listado: pendiente de que el usuario escriba el código, comprobándolo
 * contra la editorial, guardada o rechazada.
 */
type EstadoFila = "pendiente" | "guardando" | "valida" | "invalida";

/** Bloques de caracteres del código de licencia: XXXXX-XXXXX-XXXXX. */
const BLOQUES_CODIGO = 3;
const LONGITUD_BLOQUE = 5;
const LONGITUD_CODIGO = BLOQUES_CODIGO * LONGITUD_BLOQUE;

/**
 * Evento de ventana con el que se avisa de que se han guardado credenciales fuera del botón de la
 * home (p. ej. desde el aviso de acceso a un libro), para que el botón recargue sus pendientes.
 */
const EVENTO_CREDENCIALES_GUARDADAS = "shub:credenciales-licencia-guardadas";

/**
 * Avisa a la home de que se han guardado credenciales desde fuera del botón "Introducir licencias".
 */
export function notificarCredencialesGuardadas() {
    window.dispatchEvent(new Event(EVENTO_CREDENCIALES_GUARDADAS));
}

/**
 * Aplica la máscara XXXXX-XXXXX-XXXXX a lo escrito o pegado: descarta lo que no sea letra o
 * número, pasa a mayúsculas, corta en 15 caracteres e inserta los guiones.
 * @param valor Texto tal cual lo ha escrito el usuario.
 */
function formatearCodigoLicencia(valor: string): string {
    var limpio = (valor || "").toUpperCase().replace(/[^A-Z0-9]/g, "").substring(0, LONGITUD_CODIGO);
    var bloques: string[] = [];

    for (var i = 0; i < limpio.length; i += LONGITUD_BLOQUE) {
        bloques.push(limpio.substring(i, i + LONGITUD_BLOQUE));
    }

    return bloques.join("-");
}

/** True si el código tiene los 15 caracteres de la máscara. */
function esCodigoLicenciaCompleto(valor: string): boolean {
    return (valor || "").replace(/-/g, "").length == LONGITUD_CODIGO;
}

interface FilaLicencia {
    licencia: LicenciaSinCredencialItem;
    valor: string;
    estado: EstadoFila;
    mensaje: string;

    /** False cuando la credencial se guardó sin que la editorial llegara a validarla (LTI 1.3). */
    comprobada: boolean;
}

interface TablaLicenciasPendientesProps {
    /** Licencias a mostrar. Se leen solo al montar: para recargarlas, cambiar la key. */
    licencias: LicenciaSinCredencialItem[];

    /** Se invoca cada vez que la editorial acepta (o se guarda sin comprobar) un código. */
    onCredencialGuardada?: () => void;
}

interface TablaLicenciasPendientesState {
    filas: FilaLicencia[];
}

/**
 * Tabla con un campo por licencia para teclear el código impreso en el libro y guardarlo,
 * mostrando en cada fila el resultado de la comprobación contra la editorial.
 */
export class TablaLicenciasPendientes extends React.Component<TablaLicenciasPendientesProps, TablaLicenciasPendientesState> {

    public constructor(props: TablaLicenciasPendientesProps) {
        super(props);

        this.state = {
            filas: (props.licencias || []).map(licencia => {
                return {
                    licencia: licencia,
                    valor: "",
                    estado: "pendiente" as EstadoFila,
                    mensaje: null,
                    comprobada: true
                };
            })
        };
    }

    protected _cloneStateForSetState(): TablaLicenciasPendientesState {
        return cloneDeep(this.state) as TablaLicenciasPendientesState;
    }

    private _onCambiaValor(index: number, valor: string) {
        var state = this._cloneStateForSetState();
        state.filas[index].valor = formatearCodigoLicencia(valor);

        // Al reeditar un código rechazado vuelve a estar pendiente, para poder guardarlo de nuevo.
        if (state.filas[index].estado == "invalida") {
            state.filas[index].estado = "pendiente";
            state.filas[index].mensaje = null;
        }

        this.setState(state);
    }

    /**
     * Guarda el código de una fila y muestra en ella el resultado de la comprobación.
     */
    private _onGuardarFila(index: number) {
        var state = this._cloneStateForSetState();
        state.filas[index].estado = "guardando";
        state.filas[index].mensaje = null;

        this.setState(state, () => {
            var fila = this.state.filas[index];
            var service = new IntroducirLicenciasService();

            service.guardarCredencial(fila.licencia.licenciaContenidoId, (fila.valor || "").trim())
                .then(response => {
                    var nuevoState = this._cloneStateForSetState();
                    nuevoState.filas[index].estado = response.valida ? "valida" : "invalida";
                    nuevoState.filas[index].comprobada = response.comprobada;
                    nuevoState.filas[index].mensaje = response.valida
                        ? null
                        : (response.mensaje || I18n.Strings.introducirLicencias.licenciaInvalida);

                    this.setState(nuevoState, () => {
                        if (response.valida && this.props.onCredencialGuardada) {
                            this.props.onCredencialGuardada();
                        }
                    });
                })
                .catch(error => {
                    // El error se muestra en la propia fila para no perder lo que ha escrito.
                    var nuevoState = this._cloneStateForSetState();
                    nuevoState.filas[index].estado = "invalida";
                    nuevoState.filas[index].mensaje = logAndExtractInfoFromException(error);

                    this.setState(nuevoState);
                });
        });
    }

    public render(): JSX.Element {

        return <div className="table-responsive">
            <table className="table table-striped table-sm">
                <thead>
                    <tr>
                        <th>{I18n.Strings.introducirLicencias.columnaProducto}</th>
                        <th>{I18n.Strings.introducirLicencias.columnaLicencia}</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {this.state.filas.map((fila, index) => this._pintarFila(fila, index))}
                </tbody>
            </table>
        </div>;
    }

    private _pintarFila(fila: FilaLicencia, index: number): JSX.Element {

        var codigoCompleto = esCodigoLicenciaCompleto(fila.valor);
        var bloqueada = fila.estado == "guardando" || fila.estado == "valida";

        return <tr key={fila.licencia.licenciaContenidoId}>
            <td>
                {fila.licencia.titulo}
                {fila.licencia.clase && <small className="d-block text-muted">{fila.licencia.clase}</small>}
            </td>
            <td>
                <Input
                    type="text"
                    maxLength={LONGITUD_CODIGO + BLOQUES_CODIGO - 1}
                    pattern="[A-Z0-9]{5}-[A-Z0-9]{5}-[A-Z0-9]{5}"
                    autoComplete="off"
                    spellCheck={false}
                    disabled={bloqueada}
                    placeholder={I18n.Strings.introducirLicencias.placeholderLicencia}
                    className="form-control"
                    value={fila.valor || ""}
                    onChange={(evt) => this._onCambiaValor(index, evt.target.value)}
                />
                {fila.estado == "invalida" &&
                    <small className="text-danger d-block">
                        <CancelIcon cssClass="pe-1" />
                        {fila.mensaje || I18n.Strings.introducirLicencias.licenciaInvalida}
                    </small>
                }
            </td>
            <td className="text-end">
                {fila.estado == "guardando" && <SpinnerIcon />}

                {fila.estado == "valida" && fila.comprobada &&
                    <span className="text-success">
                        <CheckCircleIcon cssClass="pe-1" />
                        {I18n.Strings.introducirLicencias.licenciaCorrecta}
                    </span>
                }

                {/* LTI 1.3: el código se ha guardado pero la editorial no lo ha comprobado todavía. */}
                {fila.estado == "valida" && !fila.comprobada &&
                    <span className="text-muted">
                        <CheckIcon cssClass="pe-1" />
                        {I18n.Strings.introducirLicencias.credencialGuardada}
                    </span>
                }

                {!bloqueada && codigoCompleto &&
                    <Button color="primary" className="btn btn-rounded"
                        onClick={() => this._onGuardarFila(index)}>
                        {I18n.Strings.introducirLicencias.guardar}
                    </Button>
                }
            </td>
        </tr>;
    }
}

interface IntroducirLicenciasProps {
    /** Se invoca al cerrar el modal si se ha guardado alguna credencial. */
    onCredencialesGuardadas?: () => void;
}

interface IntroducirLicenciasState {
    showModal: boolean;
    licencias: LicenciaSinCredencialItem[];
    huboGuardado: boolean;
}

/**
 * Botón de la home que permite al usuario introducir él mismo los códigos de licencia impresos en
 * sus libros. Solo se pinta si tiene licencias digitales pendientes de credencial.
 */
export class IntroducirLicencias extends React.Component<IntroducirLicenciasProps, IntroducirLicenciasState> {

    public constructor(props: IntroducirLicenciasProps) {
        super(props);

        this.state = {
            showModal: false,
            licencias: [],
            huboGuardado: false
        };
    }

    public componentDidMount() {
        window.addEventListener(EVENTO_CREDENCIALES_GUARDADAS, this._onCredencialesGuardadasFuera);
        this._cargarPendientes();
    }

    public componentWillUnmount() {
        window.removeEventListener(EVENTO_CREDENCIALES_GUARDADAS, this._onCredencialesGuardadasFuera);
    }

    /**
     * Credenciales guardadas desde otro sitio de la página: se recargan los pendientes para que el
     * botón desaparezca si ya no queda ninguno.
     */
    private _onCredencialesGuardadasFuera = () => {
        if (this.props.onCredencialesGuardadas) {
            this.props.onCredencialesGuardadas();
        }

        this._cargarPendientes();
    };

    /**
     * Carga las licencias pendientes de credencial del curso escolar activo.
     */
    private _cargarPendientes() {
        var service = new IntroducirLicenciasService();

        service.getPendientesCursoActivo()
            .then(licencias => this.setState({ licencias: licencias }))
            .catch(error => AlertsService.showError(error));
    }

    private _onCerrar() {
        this.setState({ showModal: false }, () => {
            if (this.state.huboGuardado && this.props.onCredencialesGuardadas) {
                this.props.onCredencialesGuardadas();
            }

            this.setState({ huboGuardado: false });
            this._cargarPendientes();
        });
    }

    public render(): JSX.Element {

        // Sin licencias pendientes no hay nada que introducir: ni botón ni modal.
        if (this.state.licencias.length == 0) {
            return null;
        }

        return <React.Fragment>
            <div className="introducir-licencias d-inline-block">
                <Button className="btn btn-rounded" onClick={() => this.setState({ showModal: true })}>
                    <i className="bi bi-key"></i>
                    {I18n.Strings.introducirLicencias.title}
                </Button>
            </div>

            {this.state.showModal && this._pintarModal()}
        </React.Fragment>;
    }

    private _pintarModal(): JSX.Element {

        return <Modal isOpen={true} className="modal-lg"
            toggle={() => this._onCerrar()}
        >
            <ModalHeader close={<button className="btn-close" onClick={() => this._onCerrar()} />}>
                {I18n.Strings.introducirLicencias.title}
            </ModalHeader>
            <ModalBody>
                <p>{I18n.Strings.introducirLicencias.descripcion}</p>

                <TablaLicenciasPendientes
                    licencias={this.state.licencias}
                    onCredencialGuardada={() => this.setState({ huboGuardado: true })}
                />
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={() => this._onCerrar()}>
                    {I18n.Strings.introducirLicencias.cerrar}
                </Button>
            </ModalFooter>
        </Modal>;
    }
}

export class IntroducirLicenciasService {

    /**
     * Licencias digitales del usuario pendientes de que introduzca su código.
     * @param cursoEscolarId Curso escolar activo.
     */
    public getPendientes(cursoEscolarId: number): Promise<GetLicenciasSinCredencialesResponse> {
        var apiFetcher = new ApiFetcher();
        return apiFetcher.getByUrl<GetLicenciasSinCredencialesResponse>(
            `intranet/licencias/mias/${cursoEscolarId}/sinCredenciales`);
    }

    /**
     * Licencias pendientes de código del curso escolar activo; lista vacía si no hay curso activo.
     */
    public getPendientesCursoActivo(): Promise<LicenciaSinCredencialItem[]> {
        if (ClientContext.Current.cursoEscolarActivoId == null) {
            return Promise.resolve([]);
        }

        return this.getPendientes(ClientContext.Current.cursoEscolarActivoId)
            .then(response => response.licencias || []);
    }

    /**
     * Licencias pendientes de código con el mismo ISBN que la indicada, incluida ella misma. Lista
     * vacía si la licencia no está pendiente (ya tiene código o su tipo de acceso no lo admite).
     * @param licenciaContenidoId Licencia a la que el usuario ha intentado acceder.
     */
    public getPendientesMismoIsbn(licenciaContenidoId: number): Promise<LicenciaSinCredencialItem[]> {
        return this.getPendientesCursoActivo()
            .then(licencias => {
                var licencia = licencias.find(l => l.licenciaContenidoId == licenciaContenidoId);
                if (licencia == null) {
                    return [];
                }

                return licencias.filter(l => l.licenciaContenidoId == licenciaContenidoId
                    || (licencia.isbn != null && l.isbn == licencia.isbn));
            });
    }

    /**
     * Guarda el código introducido para una licencia, comprobándolo antes contra la editorial.
     * @param licenciaId Licencia a la que pertenece el código.
     * @param credencial Código introducido por el usuario.
     */
    public guardarCredencial(licenciaId: number, credencial: string): Promise<GuardarCredencialLicenciaResponse> {
        var apiFetcher = new ApiFetcher();
        return apiFetcher.post<{ credencial: string }, GuardarCredencialLicenciaResponse>(
            `intranet/licencias/mias/${licenciaId}/credencial`, { credencial: credencial });
    }
}
